import { Navigate, Route, Routes, useParams } from "react-router";

import { AuthProvider } from "@/auth/AuthProvider";
import { RequireAuth } from "@/auth/RequireAuth";
import { AcceptInviteScreen } from "@/auth/AcceptInviteScreen";
import { ForgotPasswordScreen, ResetPasswordScreen } from "@/auth/PasswordScreens";
import { StaffLinkScreen } from "@/auth/StaffLinkScreen";
import { SignInScreen } from "@/auth/SignInScreen";
import { AuditScreen } from "@/screens/AuditScreen";
import { NewOrganizationScreen } from "@/screens/platform/NewOrganizationScreen";
import { OrganizationScreen as PlatformOrganizationScreen } from "@/screens/platform/OrganizationScreen";
import { OrganizationsScreen } from "@/screens/platform/OrganizationsScreen";
import { DocumentResolver } from "@/screens/DocumentResolver";
import { CodeStandardTab } from "@/screens/knowledge/CodeStandardTab";
import { KnowledgeScreen } from "@/screens/knowledge/KnowledgeScreen";
import { MembersScreen } from "@/screens/MembersScreen";
import { OrganizationScreen, ProfileScreen } from "@/screens/SettingsScreens";
import { ScoreTab } from "@/screens/knowledge/ScoreTab";
import { SourceScreen } from "@/screens/knowledge/SourceScreen";
import { SourcesTab } from "@/screens/knowledge/SourcesTab";
import { TypesTab } from "@/screens/knowledge/TypesTab";
import { NotFoundScreen } from "@/screens/NotFoundScreen";
import { ProcessingScreen } from "@/screens/ProcessingScreen";
import { SelectSheetsScreen } from "@/screens/SelectSheetsScreen";
import { SetsScreen } from "@/screens/SetsScreen";
import { TakeoffScreen } from "@/screens/TakeoffScreen";
import { TypeReviewScreen } from "@/screens/TypeReviewScreen";
import { UploadScreen } from "@/screens/UploadScreen";
import { WorkspaceScreen } from "@/screens/WorkspaceScreen";
import { AppShell } from "@/shell/AppShell";
import { PlatformShell } from "@/shell/PlatformShell";

/** Retired screens' links still land somewhere: the set's current step. */
function ToDocument() {
  const { id = "" } = useParams();
  return <Navigate to={`/documents/${id}`} replace />;
}

/** The retired full review's link opens the run's take-off. */
function ToTakeoff() {
  const { id = "", runId = "" } = useParams();
  return <Navigate to={`/documents/${id}/runs/${runId}/takeoff`} replace />;
}

/**
 * Routes only; `main.tsx` supplies the browser router so tests can use a memory one.
 * Sign-in stands alone; every other page needs a signed-in user (ADR-0012) and sits in
 * the app shell (ADR-0013). Darkstar staff have the platform console under `/platform`,
 * in its own shell, and nothing else (ADR-0015). A set's guided flow is choose sheets,
 * process, take-off, then one type at a time; `/documents/:id` sends a set to whichever
 * step it has reached.
 */
export function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/sign-in" element={<SignInScreen />} />
        <Route path="/invite" element={<AcceptInviteScreen />} />
        <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
        <Route path="/reset-password" element={<ResetPasswordScreen />} />
        <Route path="/staff-link" element={<StaffLinkScreen />} />
        <Route element={<RequireAuth staff />}>
          <Route element={<PlatformShell />}>
            <Route path="/platform" element={<Navigate to="/platform/orgs" replace />} />
            <Route path="/platform/orgs" element={<OrganizationsScreen />} />
            <Route path="/platform/orgs/new" element={<NewOrganizationScreen />} />
            <Route path="/platform/orgs/:id" element={<PlatformOrganizationScreen />} />
          </Route>
        </Route>
        <Route element={<RequireAuth />}>
          <Route element={<AppShell />}>
            <Route index element={<Navigate to="/sets" replace />} />
            <Route path="/sets" element={<SetsScreen />} />
            <Route path="/sets/new" element={<UploadScreen />} />
            <Route path="/members" element={<MembersScreen />} />
            <Route path="/organization" element={<OrganizationScreen />} />
            <Route path="/profile" element={<ProfileScreen />} />
            <Route path="/audit" element={<AuditScreen />} />
            <Route path="/knowledge" element={<KnowledgeScreen />}>
              <Route index element={<Navigate to="/knowledge/sources" replace />} />
              <Route path="sources" element={<SourcesTab />} />
              <Route path="types" element={<TypesTab />} />
              <Route path="code-standard" element={<CodeStandardTab />} />
              <Route path="score" element={<ScoreTab />} />
            </Route>
            <Route path="/knowledge/sources/:id" element={<SourceScreen />} />
            <Route path="/library" element={<Navigate to="/knowledge/types" replace />} />
            <Route path="/documents/:id" element={<DocumentResolver />} />
            <Route path="/documents/:id/sheets" element={<SelectSheetsScreen />} />
            <Route path="/documents/:id/all" element={<ToDocument />} />
            <Route path="/documents/:id/sheets/:page" element={<ToDocument />} />
            <Route path="/documents/:id/runs/:runId/progress" element={<ProcessingScreen />} />
            <Route path="/documents/:id/runs/:runId/review/:page?" element={<WorkspaceScreen />} />
            <Route path="/documents/:id/runs/:runId/takeoff" element={<TakeoffScreen />} />
            <Route
              path="/documents/:id/runs/:runId/types/:unitId?"
              element={<TypeReviewScreen />}
            />
            <Route path="/documents/:id/runs/:runId" element={<ToTakeoff />} />
            <Route path="*" element={<NotFoundScreen />} />
          </Route>
        </Route>
      </Routes>
    </AuthProvider>
  );
}
